import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PadroncinoSelect } from "@/components/PadroncinoSelect";
import { toast } from "sonner";

export const PAG_CAT = [
  { value: "padroncini", label: "Padroncini" }, { value: "assicurazioni", label: "Assicurazioni" },
  { value: "officine", label: "Officine" }, { value: "commercialista", label: "Commercialista" },
  { value: "f24", label: "F24" }, { value: "fornitori", label: "Fornitori vari" },
];

const F = ({ label, children, wide }) => (
  <div className={wide ? "col-span-2" : ""}><Label className="text-xs font-heading uppercase tracking-wider">{label}</Label><div className="mt-1">{children}</div></div>
);

export const PagamentoDialog = ({ open, onClose, onSaved, edit, company, categoria, padroncini, banche }) => {
  const [f, setF] = useState({});
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setFiles([]);
    setF(edit ? { ...edit } : { company: company === "all" ? "golmar" : company, categoria: categoria === "tutti" ? "fornitori" : categoria,
      modalita: "saldo", n_rate: 2, data_scadenza: new Date().toISOString().slice(0, 10), importo: "" });
  }, [open]); // eslint-disable-line
  const s = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async () => {
    setBusy(true);
    try {
      let target;
      if (edit) target = (await api.patch(`/pagamenti/${edit.id}`, f)).data;
      else target = (await api.post("/pagamenti", f)).data.pagamenti[0];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file); fd.append("etichetta", "Fattura"); fd.append("serie", "true");
        await api.post(`/allegati/pagamenti/${target.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(edit ? "Pagamento aggiornato" : "Pagamento inserito"); onSaved();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    setBusy(false);
  };
  const rate = f.modalita !== "saldo" && !edit;
  const imp = parseFloat(f.importo) || 0;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{edit ? `Modifica pagamento${edit.rate_tot > 1 ? ` (rata ${edit.rata_n}/${edit.rate_tot})` : ""}` : "Nuovo pagamento da fare"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {!edit && <F label="Azienda"><Select value={f.company} onValueChange={s("company")}>
            <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
            <SelectContent>{["golmar", "vitam", "rotta66", "fmg"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select></F>}
          <F label="Categoria"><Select value={f.categoria} onValueChange={s("categoria")}>
            <SelectTrigger className="h-8" data-testid="pag-field-categoria"><SelectValue /></SelectTrigger>
            <SelectContent>{PAG_CAT.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select></F>
          {f.categoria === "padroncini"
            ? <F label="Padroncino"><PadroncinoSelect value={f.padroncino_id || ""} onChange={s("padroncino_id")} className="h-8 w-full"
                 padroncini={padroncini.filter((p) => p.company === f.company)} placeholder="Scegli padroncino" testId="pag-field-padroncino" /></F>
            : <F label="Beneficiario"><Input className="h-8" value={f.beneficiario || ""} onChange={s("beneficiario")} placeholder="es. Officina Rossi, Agenzia Entrate" data-testid="pag-field-beneficiario" /></F>}
          <F label="Descrizione" wide><Input className="h-8" value={f.descrizione || ""} onChange={s("descrizione")} placeholder="es. Tagliando, F24 IVA, Polizza RC" data-testid="pag-field-descrizione" /></F>
          <F label="N. fattura"><Input className="h-8" value={f.numero_fattura || ""} onChange={s("numero_fattura")} data-testid="pag-field-fattura" /></F>
          <F label="Codice avviso / rif. Webdesk"><Input className="h-8" value={f.codice_avviso || ""} onChange={s("codice_avviso")} data-testid="pag-field-codice" /></F>
          <F label={f.modalita === "rate" ? "Importo totale (€)" : f.modalita === "mensile" ? "Importo mensile (€)" : "Importo (€)"}>
            <Input type="number" step="0.01" className="h-8" value={f.importo ?? ""} onChange={s("importo")} data-testid="pag-field-importo" /></F>
          <F label={rate ? "Prima scadenza" : "Scadenza"}><Input type="date" className="h-8" value={f.data_scadenza || ""} onChange={s("data_scadenza")} data-testid="pag-field-data" /></F>
          {!edit && <F label="Modalità"><Select value={f.modalita} onValueChange={s("modalita")}>
            <SelectTrigger className="h-8" data-testid="pag-field-modalita"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="saldo">Saldo unico</SelectItem>
              <SelectItem value="rate">Rateizzato (divide il totale)</SelectItem>
              <SelectItem value="mensile">Mensile ricorrente</SelectItem>
            </SelectContent>
          </Select></F>}
          {rate && <F label={f.modalita === "rate" ? "Numero rate" : "Numero mesi"}>
            <Input type="number" min="2" max="120" className="h-8" value={f.n_rate} onChange={s("n_rate")} data-testid="pag-field-nrate" /></F>}
          {rate && <div className="col-span-2 text-xs text-muted-foreground" data-testid="pag-rate-preview">
            {f.modalita === "rate" ? `${f.n_rate} rate mensili da circa ${(imp / (parseInt(f.n_rate) || 1)).toFixed(2)} €` : `${f.n_rate} pagamenti mensili da ${imp.toFixed(2)} € (totale ${(imp * (parseInt(f.n_rate) || 0)).toFixed(2)} €)`}, a partire dalla prima scadenza.
          </div>}
          <F label="Banca di pagamento"><Select value={f.banca || "__none"} onValueChange={(v) => setF({ ...f, banca: v === "__none" ? null : v })}>
            <SelectTrigger className="h-8" data-testid="pag-field-banca"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="__none">—</SelectItem>{banche.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
          </Select></F>
          <F label="Allega PDF fattura"><Input type="file" multiple accept=".pdf,image/*" className="h-8 text-xs" onChange={(e) => setFiles([...e.target.files])} data-testid="pag-field-files" /></F>
          <F label="Note" wide><Input className="h-8" value={f.note || ""} onChange={s("note")} data-testid="pag-field-note" /></F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={busy} data-testid="pag-save-button">{busy ? "Salvataggio…" : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
